import { ReactNode, useCallback, useEffect, useRef } from 'react'
import { Box, Stack, Switch, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import InfoIcon from '@mui/icons-material/Info'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from '../../../components'
import * as sxStyles from './layout.styles'
import { useDisableApplyMethod } from '../utils/helpers'
import { TAB_KEYS } from '../utils/consts'
import {
  discardUnsavedFilters,
  hydrateDraftsForActiveTab,
  toggleApplyGlobally,
} from '../../../store'
import { selectIsApplyGlobally } from '../../../store/selectors/dataAnalysisSelectors'
import { ExportControls } from './ExportControls'

interface DataTabsLayoutProps {
  sidebar: ReactNode
  children: ReactNode
  onApply?: () => void
  tabKey?: TAB_KEYS
}

export const DataTabsLayout = ({
  sidebar,
  children,
  onApply,
  tabKey,
}: DataTabsLayoutProps) => {
  const dispatch = useDispatch()
  const { isApplyDisabled, infoMessage } = useDisableApplyMethod(tabKey)
  const isApplyGlobally = useSelector(selectIsApplyGlobally)
  const contentRef = useRef<HTMLDivElement | null>(null)

  const handleToggleApplyGlobally = useCallback(
    (event) => {
      dispatch(toggleApplyGlobally(event.target.checked))
    },
    [dispatch]
  )

  // Handle discard unsaved filters logic here
  useEffect(() => {
    if (!tabKey) return
    // First discard any unsaved changes (both global and local drafts)
    dispatch(discardUnsavedFilters())
    // Then hydrate the active context draft from its last applied values
    dispatch(hydrateDraftsForActiveTab({ key: tabKey }))
  }, [tabKey])

  return (
    <Box sx={sxStyles.root}>
      <ExportControls rootEl={contentRef.current} tabKey={tabKey} />
      <Grid container spacing={2} alignItems="flex-start" width="100%">
        <Grid
          size={{ xs: 12, md: 4, lg: 4, xl: 3 }}
          sx={sxStyles.sideBarGridWrapper}
        >
          <Box sx={sxStyles.sidebar}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Stack
                sx={{ alignItems: 'center', gap: 1, flexDirection: 'row' }}
              >
                <Typography fontSize="small">Apply Globally</Typography>
                <Switch
                  checked={isApplyGlobally}
                  onChange={handleToggleApplyGlobally}
                />
              </Stack>
              <Button
                sx={sxStyles.applyButton}
                variantType="primary"
                onClick={onApply}
                disabled={tabKey && isApplyDisabled}
              >
                Apply
              </Button>
            </Box>
            <Box>{sidebar}</Box>
            <Box sx={sxStyles.buttonContainer}>
              {tabKey && infoMessage && isApplyDisabled && (
                <Box sx={sxStyles.infoMessageContainer}>
                  <InfoIcon sx={sxStyles.infoIcon} />
                  <span>{infoMessage}</span>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid
          size={{ xs: 12, md: 8, lg: 8, xl: 9 }}
          sx={sxStyles.contentGridWrapper}
        >
          <Box
            ref={contentRef}
            sx={{ width: '100%', position: 'relative', minHeight: '100%' }}
          >
            {children}
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}
